// Context for Pharma users
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { UserInfo } from '../features/auth/types';


// Site row as returned by the backend GetPharma1Sites (vue_api log_in -> sub_users).
// is_internal (0/1) marks INTERNAL_SITE test sites (e.g. DFI18231_0000000) that only
// VV operators / admins ever receive - used to tag them "VV only" in the site selector.
// is_uat (0/N) marks UAT site accounts (legacy 'UAT' or study-scoped '{TRIAL}_UAT',
// e.g. DFI18231_9999999); visible to all study roles but grouped at the bottom.
export type PharmaSubUser = { user_id: number; user_name: string; is_internal?: number; is_uat?: number };

export type Pharma = {
  user: UserInfo | null;
  plainP: string;
  setPlainP: React.Dispatch<React.SetStateAction<string>>;
  setUser: React.Dispatch<React.SetStateAction<UserInfo | null>>;
  subUsers: PharmaSubUser[];
  setSubUsers: React.Dispatch<React.SetStateAction<PharmaSubUser[]>>;
  selectedSubUser: PharmaSubUser | null;
  setSelectedSubUser: React.Dispatch<React.SetStateAction<PharmaSubUser | null>>;
  isSiteLoggedIn: boolean;
  setIsSiteLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  flags: string[];
  setFlags: React.Dispatch<React.SetStateAction<string[]>>;
  isPV: boolean;
  setIsPV: React.Dispatch<React.SetStateAction<boolean>>;
  isCRC: boolean;
  setIsCRC: React.Dispatch<React.SetStateAction<boolean>>;
  isREADONLY: boolean;
  setIsREADONLY: React.Dispatch<React.SetStateAction<boolean>>;
  pharmaLogout: () => void;
};

export const PharmaContext = createContext<Pharma | null>(null);

export const usePharma = () => {
  const context = useContext(PharmaContext);
  if (!context) {
    throw new Error('usePharma must be used within a PharmaProvider');
  }
  return context;
};

export const PharmaProvider: React.FC = ({ children }) => {
  const [user, setUser] = useSessionStorage<UserInfo | null>('pharmaUser', null);
  const [plainP, setPlainP] = useSessionStorage<string>('pharmaPlainP', '');
  const [subUsers, setSubUsers] = useSessionStorage<PharmaSubUser[]>('pharmaSubUsers', []);
  const [selectedSubUser, setSelectedSubUser] = useSessionStorage<PharmaSubUser | null>('pharmaSelectedSubUser', null);
  const [isSiteLoggedIn, setIsSiteLoggedIn] = useSessionStorage<boolean>('pharmaIsSiteLoggedIn', false);

  const [flags, setFlags] = useSessionStorage<string[]>('pharmaFlags', []);
  const [isPV, setIsPV] = useSessionStorage<boolean>('pharmaIsPV', false);
  const [isCRC, setIsCRC] = useSessionStorage<boolean>('pharmaIsCRC', false);
  const [isREADONLY, setIsREADONLY] = useSessionStorage<boolean>('pharmaIsREADONLY', false);

  const pharmaLogout = useCallback(() => {
    setUser(null);
    // Clear the cached plaintext password too - a true logout must leave no credential behind.
    setPlainP('');
    setSubUsers([]);
    setSelectedSubUser(null);
    setIsSiteLoggedIn(false);
    setFlags([]);
    setIsPV(false);
    setIsCRC(false);
    setIsREADONLY(false);
    // `u` / `p` are raw sessionStorage keys written directly by Login (not provider state),
    // so they have to be removed explicitly here alongside the pharma* keys.
    window.sessionStorage.removeItem('u');
    window.sessionStorage.removeItem('p');
  }, [setUser, setPlainP, setSubUsers, setSelectedSubUser, setIsSiteLoggedIn, setFlags, setIsPV, setIsCRC, setIsREADONLY]);


  return (
    <PharmaContext.Provider value={{ user, setUser, plainP, setPlainP, subUsers, selectedSubUser, isSiteLoggedIn, setIsSiteLoggedIn, setSelectedSubUser, setSubUsers, flags, setFlags, isPV, setIsPV, isCRC, setIsCRC, isREADONLY, setIsREADONLY, pharmaLogout }}>
      {children}
    </PharmaContext.Provider>
  );
};


function useSessionStorage<T>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  // Get the initial value from session storage or use the provided initialValue
  const [value, setValue] = useState(() => {
    const storedValue = sessionStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : initialValue;
  });

  // Update session storage whenever the value changes
  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}
